import React, { useContext } from 'react';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Badge from 'react-bootstrap/Badge';
import Container from 'react-bootstrap/Container';
import { Redirect, Route, Link } from 'react-router-dom';
import intersection from 'lodash/intersection';
import { loginRedirect, logout, logoutRedirect } from '../util/AuthUtils';
import WebUtils from '../util/WebUtils';
import { locationInPath } from '../util/NavUtils';
import '../../css/HyruleNav.css';
import LocalizationContext from '../context/LocalizationContext';
const NULL_STR = 'null';
/**
 * Checks the list of user scopes against a component's required scopes. Returns true if the user has a matching
 * scope for the component, or if the component didn't define any required scopes
 * @param userScopes - The user's scopes
 * @param componentScopes - The component's scopes
 */
const checkScope = (userScopes, componentScopes) => !componentScopes || intersection(userScopes, componentScopes).length > 0;

/**
 * Creates an array of navigation dropdown items
 * @param userDropDownItems - An array of DropdownItems to be rendered
 * @param userScopes - The user's scopes
 * @returns An array of JSX Elements representing the navigation dropdown items
 */
const getDropDownItems = (userDropDownItems, userScopes) => userDropDownItems.filter(item => checkScope(userScopes, item.scopes)).map(item => /*#__PURE__*/React.createElement(NavDropdown.Item, {
  as: "button",
  onClick: item.onClick,
  "data-testid": item.testId,
  key: item.key
}, item.item));

/**
 * Creates the additional user drop down items if they were specified
 * @param userScopes - The user's scopes
 * @param userDropDownItems - The user DropdownItems to render
 * @returns An array of JSX Elements that will be rendered in the user dropdown menu if any userDropDownItems were
 * provided, else null.
 */
const getAdditionalUserDropDownItems = (userScopes, userDropDownItems) => {
  if (userDropDownItems) {
    const elements = getDropDownItems(userDropDownItems, userScopes);
    if (elements.length > 0) {
      elements.push( /*#__PURE__*/React.createElement(NavDropdown.Divider, {
        key: "dropdown-elements-divider"
      }));
    }
    return elements;
  }
  return null;
};

// TODO: Refactor dropdown menu into its own component: https://jira.noa.com/browse/NLIBS-48
/**
 * Creates additional menu drop down items if they were specified
 * @param userScopes - The user's scopes
 * @param dropDownMenus - The drop down menus to render
 * @returns An array of JSX Elements that will be rendered in the menu (left of the user dropdown) if any
 * dropDownMenus were provided, else null.
 */
const getAdditionalDropDownMenus = (userScopes, dropDownMenus) => {
  if (dropDownMenus) {
    return dropDownMenus.filter(menu => checkScope(userScopes, menu.scopes)).map(menu => /*#__PURE__*/React.createElement(NavDropdown, {
      title: menu.title,
      id: menu.key,
      key: menu.key,
      "data-testid": menu.testId
    }, getDropDownItems(menu.items, userScopes)));
  }
  return null;
};
const HyruleNav = _ref => {
  let {
    authenticated,
    authToken,
    defaultPath,
    location,
    componentTabs,
    appName,
    scopes,
    userInfo,
    routeState,
    navHeaderCustom,
    history,
    match,
    userConfig,
    userDropDownItems,
    dropDownMenus,
    logout: customLogout
  } = _ref;
  const localizedStrings = useContext(LocalizationContext);
  const handleLogout = () => {
    logout();
    if (customLogout) {
      customLogout();
    }
    if (authToken) {
      logoutRedirect(authToken);
    }
  };
  const getLoginElement = () => {
    let username = '';
    if (userInfo) {
      username = userInfo.firstName ? userInfo.firstName : userInfo.userId;
    }
    return authenticated ? /*#__PURE__*/React.createElement(NavDropdown.Item, {
      as: "button",
      onClick: handleLogout
    }, `${username} (${localizedStrings.hyrule.logIn.out})`) : /*#__PURE__*/React.createElement(NavDropdown.Item, {
      as: "button",
      onClick: loginRedirect
    }, localizedStrings.hyrule.logIn.title);
  };
  const shouldRender = componentScopes => !componentScopes || intersection(scopes, componentScopes).length > 0;
  let tabs = [];
  let redirectToDefaultRoute = null;
  if (authToken !== NULL_STR && authToken) {
    tabs = componentTabs.map(item => {
      if (item.scopes === null || item.scopes.length === 0 || intersection(scopes, item.scopes).length > 0) {
        const tabPath = item.components[0].path;
        if (item.components.length === 1) {
          // If there was only one item, display it as a tab
          const component = item.components[0];
          if (shouldRender(component.scopes)) {
            const linkPath = component.entryPath ? component.entryPath : tabPath;
            return /*#__PURE__*/React.createElement(Nav.Link, {
              as: Link,
              to: linkPath,
              key: item.name,
              active: locationInPath(location.pathname, tabPath)
            }, item.name);
          }
        }
        const dropdownRoutes = [];

        // If there was more than one item, it's a drop down
        const dropdownItems = item.components.map(component => {
          if (shouldRender(component.scopes)) {
            dropdownRoutes.push(component.path);
            const linkPath = component.entryPath ? component.entryPath : component.path;
            return /*#__PURE__*/React.createElement(NavDropdown.Item, {
              as: Link,
              to: linkPath,
              key: component.name
            }, component.name);
          }
          return null;
        });

        // Check to see if a route in the current tab matches the current location
        let active = false;
        dropdownRoutes.forEach(route => {
          if (locationInPath(location.pathname, route)) {
            active = true;
          }
        });
        return /*#__PURE__*/React.createElement(NavDropdown, {
          id: item.name,
          title: item.name,
          active: active,
          key: item.name
        }, dropdownItems);
      }
      return '';
    });
    if (routeState && routeState !== '/') {
      redirectToDefaultRoute = /*#__PURE__*/React.createElement(Route, {
        exact: true,
        path: "/",
        render: () => /*#__PURE__*/React.createElement(Redirect, {
          to: routeState
        })
      });
    } else if (defaultPath !== '/') {
      redirectToDefaultRoute = /*#__PURE__*/React.createElement(Route, {
        exact: true,
        path: "/",
        render: () => /*#__PURE__*/React.createElement(Redirect, {
          to: defaultPath
        })
      });
    }
  }
  let environment = WebUtils.getEnvironmentName();
  if (environment) {
    environment = /*#__PURE__*/React.createElement(Badge, {
      id: "env-badge",
      bg: "warning",
      className: "text-light"
    }, environment);
  }
  const NavHeaderCustom = navHeaderCustom;
  return /*#__PURE__*/React.createElement("div", null, NavHeaderCustom ? authenticated && /*#__PURE__*/React.createElement(NavHeaderCustom, {
    authToken: authToken,
    appName: appName,
    authenticated: authenticated,
    defaultPath: defaultPath,
    componentTabs: componentTabs,
    scopes: scopes,
    userInfo: userInfo,
    history: history,
    location: location,
    match: match,
    routeState: routeState,
    userConfig: userConfig,
    logout: handleLogout
  }) : /*#__PURE__*/React.createElement(Navbar, {
    id: "hyrule-nav",
    "data-testid": "hyruleNav",
    variant: "dark",
    className: "hyrule-nav-bar",
    expand: "lg"
  }, /*#__PURE__*/React.createElement(Container, null, /*#__PURE__*/React.createElement(Navbar.Brand, {
    href: defaultPath,
    "data-testid": "logo-btn",
    as: Link,
    to: defaultPath
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("img", {
    className: "pr-2 d-none d-sm-inline-block mb-2",
    height: "30",
    alt: "Nintendo",
    src: require('../../assets/images/nintendo-logo-racetrack.svg')
  })), /*#__PURE__*/React.createElement("span", {
    id: "app-name"
  }, appName), " ", environment), /*#__PURE__*/React.createElement(Navbar.Toggle, {
    "aria-controls": "navbar-content"
  }), /*#__PURE__*/React.createElement(Navbar.Collapse, {
    id: "navbar-content"
  }, /*#__PURE__*/React.createElement(Nav, {
    className: "mr-auto"
  }, tabs), /*#__PURE__*/React.createElement(Nav, null, getAdditionalDropDownMenus(scopes, dropDownMenus), /*#__PURE__*/React.createElement(Nav.Link, {
    href: "/"
  }, /*#__PURE__*/React.createElement("i", {
    className: "fas fa-th fa-lg"
  })), /*#__PURE__*/React.createElement(NavDropdown, {
    title: /*#__PURE__*/React.createElement("i", {
      className: "fas fa-user fa-lg",
      "data-testid": "user-dropdown-btn"
    }),
    id: "user-settings-dropdown",
    "data-testid": "user-dropdown"
  }, getAdditionalUserDropDownItems(scopes, userDropDownItems), /*#__PURE__*/React.createElement(NavDropdown.Item, {
    as: Link,
    to: userConfig.path
  }, userConfig.name), /*#__PURE__*/React.createElement(NavDropdown.Divider, null), getLoginElement()))))), /*#__PURE__*/React.createElement("div", null, redirectToDefaultRoute));
};
export default HyruleNav;